import { Card, Title, Text, Flex, ProgressBar, Badge, Button } from '@tremor/react';
import type { IconectStatsResponse } from '../types';

interface IconectPanelProps {
  data: IconectStatsResponse | null;
  onResetStats?: () => void;
}

export function IconectPanel({ data, onResetStats }: IconectPanelProps) {
  if (!data) {
    return (
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">iConect/Xera API</Title>
        <div className="mt-4 h-48 flex items-center justify-center">
          <Text className="text-gray-500">Loading...</Text>
        </div>
      </Card>
    );
  }

  const { limiter, lifetime, recent, rateLimits, retries, config } = data;

  // Calculate RPM bar percentage based on configured RPM
  const maxRpm = config.rpm || 600;
  const rpmPct = recent.effectiveRpm ? Math.min((recent.effectiveRpm / maxRpm) * 100, 100) : 0;

  // Determine status based on various factors
  const isRateLimited = rateLimits?.isCurrentlyRateLimited || (recent.rateLimitCount ?? 0) > 0;
  const isWarning = limiter.reservoir !== null && limiter.reservoir < 100;
  const hasRecentRetries = retries && (retries.recentRecoveredCount > 0 || retries.recentFailedCount > 0);

  return (
    <Card className="bg-gray-900 ring-gray-800 h-full flex flex-col">
      <Flex justifyContent="between" alignItems="center">
        <Title className="text-gray-100">iConect/Xera API</Title>
        {isRateLimited && (
          <Badge color="red" size="sm">Rate Limited</Badge>
        )}
        {!isRateLimited && isWarning && (
          <Badge color="amber" size="sm">Low Reservoir</Badge>
        )}
        {!isRateLimited && !isWarning && (
          <Badge color="emerald" size="sm">Healthy</Badge>
        )}
      </Flex>

      <div className="mt-4 space-y-4 flex-1 pb-3">
        {/* Effective RPM */}
        <div>
          <Flex justifyContent="between" className="mb-1">
            <Text className="text-gray-400 text-sm">Effective RPM</Text>
            <Text className="text-gray-200 font-medium">
              {recent.effectiveRpm ?? 0} req/min
            </Text>
          </Flex>
          <ProgressBar
            value={rpmPct}
            color={isRateLimited ? 'red' : isWarning ? 'amber' : 'emerald'}
            className="h-2"
          />
        </div>

        {/* Limiter State */}
        <div className="grid grid-cols-3 gap-2">
          <LimiterStat label="Running" value={limiter.running} />
          <LimiterStat label="Queued" value={limiter.queued} warn={limiter.queued > 50} />
          <LimiterStat
            label="Reservoir"
            value={limiter.reservoir ?? '∞'}
            warn={limiter.reservoir !== null && limiter.reservoir < 100}
          />
        </div>

        {/* Performance Stats */}
        <div className="pt-2 border-t border-gray-800">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Text className="text-gray-500 text-xs">Success Rate</Text>
              <Text className={`font-medium ${
                parseFloat(lifetime.successRate) >= 99 ? 'text-emerald-400' : 'text-amber-400'
              }`}>
                {lifetime.successRate}
              </Text>
            </div>
            <div>
              <Text className="text-gray-500 text-xs">Avg Latency</Text>
              <Text className="text-gray-300 font-medium">
                {lifetime.avgDurationMs ? `${lifetime.avgDurationMs}ms` : '--'}
              </Text>
            </div>
          </div>
        </div>

        {/* Retries & Recovery (60s) */}
        {retries && (
          <div className="pt-2 border-t border-gray-800">
            <Text className="text-gray-500 text-xs mb-2">Retries (60s)</Text>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div>
                <Text className="text-gray-500">Attempts</Text>
                <Text className={retries.recentRecoveredCount + retries.recentFailedCount > 0 ? 'text-amber-400 font-medium' : 'text-gray-400'}>
                  {retries.recentRecoveredCount + retries.recentFailedCount}
                </Text>
              </div>
              <div>
                <Text className="text-gray-500">Recovered</Text>
                <Text className={retries.recentRecoveredCount > 0 ? 'text-emerald-400 font-medium' : 'text-gray-400'}>
                  {retries.recentRecoveredCount}
                </Text>
              </div>
              <div>
                <Text className="text-gray-500">Failed</Text>
                <Text className={retries.recentFailedCount > 0 ? 'text-red-400 font-medium' : 'text-gray-400'}>
                  {retries.recentFailedCount}
                </Text>
              </div>
            </div>
            {hasRecentRetries && (
              <Text className="text-gray-500 text-xs mt-1 text-center">
                {retries.serverErrors > 0 && `${retries.serverErrors} server`}
                {retries.serverErrors > 0 && (retries.timeoutErrors > 0 || retries.networkErrors > 0) && ', '}
                {retries.timeoutErrors > 0 && `${retries.timeoutErrors} timeout`}
                {retries.timeoutErrors > 0 && retries.networkErrors > 0 && ', '}
                {retries.networkErrors > 0 && `${retries.networkErrors} network`}
              </Text>
            )}
          </div>
        )}

        {/* Rate Limit Info */}
        <div className="pt-2 border-t border-gray-800">
          <Flex justifyContent="between" alignItems="center">
            <Text className="text-gray-500 text-xs">Rate Limits (60s)</Text>
            <Text className={(recent.rateLimitCount ?? 0) > 0 ? 'text-red-400' : 'text-emerald-400'}>
              {recent.rateLimitCount ?? 0}
            </Text>
          </Flex>
        </div>

        {/* Lifetime Stats */}
        <div className="pt-2 border-t border-gray-800">
          <Text className="text-gray-500 text-xs mb-2">Lifetime</Text>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Flex justifyContent="between">
              <span className="text-gray-500">Requests</span>
              <span className="text-gray-400">{lifetime.totalRequests.toLocaleString()}</span>
            </Flex>
            <Flex justifyContent="between">
              <span className="text-gray-500">Rate Limits</span>
              <span className="text-gray-400">{lifetime.totalRateLimited ?? 0}</span>
            </Flex>
            {retries && (
              <>
                <Flex justifyContent="between">
                  <span
                    className="text-gray-500 cursor-help underline decoration-dotted decoration-gray-600"
                    title="Total retry attempts (rate limit hits). A single request can trigger multiple retries."
                  >
                    Retries
                  </span>
                  <span className="text-gray-400">{retries.totalRetries}</span>
                </Flex>
                <Flex justifyContent="between">
                  <span
                    className="text-gray-500 cursor-help underline decoration-dotted decoration-gray-600"
                    title="Recovery Rate: Of requests that needed retries, what % eventually succeeded. Formula: Recovered / (Recovered + Failed)"
                  >
                    Recovery Rate
                  </span>
                  <span className={retries.totalFailed > 0 ? 'text-amber-400' : 'text-emerald-400'}>
                    {retries.recoveryRate}
                  </span>
                </Flex>
              </>
            )}
          </div>
        </div>

        {/* Configured Limits */}
        {config && (
          <div className="pt-2 border-t border-gray-800">
            <Text className="text-gray-500 text-xs mb-2">Configured Limits</Text>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <Flex justifyContent="between">
                <span className="text-gray-500">RPM</span>
                <span className="text-gray-400">{config.rpm?.toLocaleString()}</span>
              </Flex>
              <Flex justifyContent="between">
                <span className="text-gray-500">Concurrent</span>
                <span className="text-gray-400">{config.concurrent}</span>
              </Flex>
              <Flex justifyContent="between">
                <span className="text-gray-500">Min Time</span>
                <span className="text-gray-400">{config.minTimeMs}ms</span>
              </Flex>
            </div>
          </div>
        )}

      </div>

      {/* Reset Button */}
      {onResetStats && (
        <div className="pt-3 border-t border-gray-800 mt-auto">
          <Button
            size="xs"
            variant="secondary"
            className="w-full bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700 hover:text-gray-300"
            onClick={onResetStats}
          >
            Reset Stats
          </Button>
        </div>
      )}
    </Card>
  );
}

interface LimiterStatProps {
  label: string;
  value: number | string;
  warn?: boolean;
}

function LimiterStat({ label, value, warn }: LimiterStatProps) {
  return (
    <div className="bg-gray-800/50 rounded p-2 text-center">
      <Text className="text-gray-500 text-xs">{label}</Text>
      <Text className={`font-semibold ${warn ? 'text-amber-400' : 'text-gray-200'}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Text>
    </div>
  );
}
